import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc.js";
import { Pagina } from "../../components/Pagina.js";
import { Botao, Falha, Girando, Selo, Vazio } from "../../components/ui.js";
import { real } from "../../lib/formato.js";
import { CartaoBrick } from "./CartaoBrick.js";

/**
 * Comprar um pacote.
 *
 * Três passos, um por tela: escolher o pacote, escolher como pagar, pagar. O
 * PIX vem primeiro porque credita em segundos e não exige dados de cartão.
 */
export function ComprarPage() {
  const [pacoteId, setPacoteId] = useState<number | null>(null);
  const [metodo, setMetodo] = useState<"pix" | "cartao" | null>(null);

  const catalogo = trpc.pagamentos.catalogo.useQuery();
  const pacote = catalogo.data?.find((p) => p.id === pacoteId) ?? null;

  return (
    <Pagina titulo="Comprar fotos" descricao="Escolha um pacote e pague sem sair daqui.">
      {catalogo.isLoading ? (
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      ) : catalogo.isError ? (
        <Falha mensagem={catalogo.error.message} />
      ) : (catalogo.data?.length ?? 0) === 0 ? (
        <Vazio
          titulo="Nenhum pacote à venda"
          descricao="Quem administra o sistema ainda não colocou pacotes à venda."
        />
      ) : pacote === null ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {catalogo.data!.map((p) => (
            <button
              key={p.id}
              onClick={() => setPacoteId(p.id)}
              className="flex flex-col gap-1 rounded-peca border border-borda bg-superficie-alta p-4 text-left transition-colors hover:border-brasa"
            >
              <span className="font-display text-[1.25rem] leading-tight">{p.nome}</span>
              <span className="text-[0.875rem] text-texto-suave">{p.fotos} fotos</span>
              <span className="mt-2 font-display text-[1.5rem] text-brasa">
                {real(p.precoCentavos / 100)}
              </span>
              <span className="text-[0.8125rem] text-texto-fraco">
                {real(p.precoCentavos / 100 / p.fotos)} por foto
              </span>
            </button>
          ))}
        </div>
      ) : metodo === null ? (
        <div className="flex flex-col gap-4">
          <Resumo pacote={pacote} aoTrocar={() => setPacoteId(null)} />

          <div className="grid gap-3 sm:grid-cols-2">
            <Botao tom="brasa" largo onClick={() => setMetodo("pix")}>
              Pagar com PIX
            </Botao>
            <Botao tom="quieto" largo onClick={() => setMetodo("cartao")}>
              Pagar com cartão
            </Botao>
          </div>

          <p className="text-[0.8125rem] leading-relaxed text-texto-fraco">
            No PIX o saldo entra em segundos. No cartão, na hora da aprovação.
          </p>
        </div>
      ) : metodo === "pix" ? (
        <PagamentoPix pacote={pacote} aoVoltar={() => setMetodo(null)} />
      ) : (
        <PagamentoCartao pacote={pacote} aoVoltar={() => setMetodo(null)} />
      )}
    </Pagina>
  );
}

type Pacote = { id: number; nome: string; fotos: number; precoCentavos: number };

function Resumo({ pacote, aoTrocar }: { pacote: Pacote; aoTrocar: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-peca border border-borda bg-superficie p-4">
      <div>
        <p className="font-display text-[1.125rem] leading-tight">{pacote.nome}</p>
        <p className="text-[0.875rem] text-texto-suave">{pacote.fotos} fotos</p>
      </div>
      <div className="text-right">
        <p className="font-display text-[1.25rem] text-brasa">{real(pacote.precoCentavos / 100)}</p>
        <button
          onClick={aoTrocar}
          className="text-[0.8125rem] text-texto-suave underline underline-offset-4"
        >
          trocar
        </button>
      </div>
    </div>
  );
}

function PagamentoPix({ pacote, aoVoltar }: { pacote: Pacote; aoVoltar: () => void }) {
  const queryClient = useQueryClient();
  const [copiado, setCopiado] = useState(false);

  const criar = trpc.pagamentos.criarPix.useMutation();

  // Cria assim que a tela abre: a pessoa já escolheu e clicou, e mostrar outro
  // botão só adiaria o QR.
  useEffect(() => {
    if (!criar.isPending && !criar.data && !criar.isError) {
      criar.mutate({ pacoteId: pacote.id });
    }
  }, [pacote.id]);

  const pagamentoId = criar.data?.pagamentoId;

  /**
   * Enquanto a tela está aberta, pergunta se já caiu.
   *
   * O webhook é a verdade — este polling só existe para a tela reagir. Sem ele,
   * a pessoa paga e continua olhando um QR que não muda.
   */
  const status = trpc.pagamentos.status.useQuery(
    { pagamentoId: pagamentoId! },
    { enabled: pagamentoId !== undefined, refetchInterval: 4000 },
  );

  useEffect(() => {
    if (status.data?.creditado) void queryClient.invalidateQueries();
  }, [status.data?.creditado, queryClient]);

  if (criar.isPending) return <Girando className="mx-auto my-12 size-6 text-texto-fraco" />;
  if (criar.isError) return <Falha mensagem={criar.error.message} />;
  if (!criar.data) return null;

  if (status.data?.creditado) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <Selo tom="fixado">pagamento confirmado</Selo>
        <p className="font-display text-[1.5rem]">As {pacote.fotos} fotos já estão na sua conta.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Resumo pacote={pacote} aoTrocar={aoVoltar} />

      <div className="flex flex-col items-center gap-4 rounded-peca border border-borda bg-superficie-alta p-5">
        {criar.data.qrCodeBase64 && (
          <img
            src={`data:image/png;base64,${criar.data.qrCodeBase64}`}
            alt="QR Code para pagamento"
            className="size-56 rounded-peca bg-white p-2"
          />
        )}

        {/* O copia-e-cola vem depois do QR mas é o que serve no celular, onde
            ninguém consegue escanear a própria tela. */}
        {criar.data.copiaECola && (
          <div className="w-full">
            <Botao
              tom="quieto"
              largo
              onClick={() => {
                void navigator.clipboard.writeText(criar.data!.copiaECola!);
                setCopiado(true);
                setTimeout(() => setCopiado(false), 2500);
              }}
            >
              {copiado ? "Código copiado" : "Copiar código PIX"}
            </Botao>
            <p className="mt-2 text-center text-[0.8125rem] text-texto-fraco">
              No celular, copie o código e cole no aplicativo do seu banco.
            </p>
          </div>
        )}

        <div className="flex items-center gap-2 text-[0.875rem] text-texto-suave">
          <Girando className="size-4" />
          Esperando o pagamento…
        </div>
      </div>
    </div>
  );
}

function PagamentoCartao({ pacote, aoVoltar }: { pacote: Pacote; aoVoltar: () => void }) {
  const queryClient = useQueryClient();
  const [resultado, setResultado] = useState<{ ok: boolean; motivo: string | null } | null>(null);

  const pagar = trpc.pagamentos.criarCartao.useMutation({
    onSuccess: async (r) => {
      setResultado({ ok: r.status === "aprovado", motivo: r.motivo });
      if (r.status === "aprovado") await queryClient.invalidateQueries();
    },
    onError: (e) => setResultado({ ok: false, motivo: e.message }),
  });

  if (resultado?.ok) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <Selo tom="fixado">pagamento aprovado</Selo>
        <p className="font-display text-[1.5rem]">As {pacote.fotos} fotos já estão na sua conta.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Resumo pacote={pacote} aoTrocar={aoVoltar} />

      {resultado?.motivo && <Falha mensagem={resultado.motivo} />}

      <CartaoBrick
        valorCentavos={pacote.precoCentavos}
        processando={pagar.isPending}
        aoEnviar={(dados) => {
          setResultado(null);
          pagar.mutate({ pacoteId: pacote.id, ...dados });
        }}
      />
    </div>
  );
}
